import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Printer, Download, MessageCircle } from "lucide-react";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Button } from "@/components/ui/button";
import { LetterSheet } from "@/project/components/LetterSheet";
import {
  orgsQuery,
  settingsQuery,
  checklistQuery,
} from "@/project/lib/queries";
import { inr, shortDate } from "@/project/lib/format";
import { printElementAsPdf, shareOnWhatsApp } from "@/project/lib/print-doc";

export const Route = createFileRoute("/letters/$id")({
  head: () => ({
    meta: [
      { title: "Letter Detail · Assistance & Letter Desk" },
      {
        name: "description",
        content: "View, print and download a generated recommendation letter.",
      },
      { property: "og:title", content: "Recommendation Letter Detail" },
      {
        property: "og:description",
        content:
          "View a generated recommendation letter for a partner organization.",
      },
    ],
  }),
  component: LetterDetailPage,
});

function LetterDetailPage() {
  const { id } = Route.useParams();
  const { data: orgs } = useQuery(orgsQuery);
  const { data: settings } = useQuery(settingsQuery);
  const { data: checklist } = useQuery(checklistQuery);

  const { data: letter, isLoading } = useQuery({
    queryKey: ["letter", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generated_letters")
        .select("*, organizations(*)")
        .eq("id", id)
        .single();
      if (error) {
        throw error;
      }
      return data;
    },
  });

  const { data: app } = useQuery({
    queryKey: ["application", letter?.application_id],
    enabled: Boolean(letter?.application_id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("*, assistance_categories(name)")
        .eq("id", letter!.application_id)
        .single();
      if (error) {
        throw error;
      }
      return data;
    },
  });

  const { data: appOrgs } = useQuery({
    queryKey: ["application-organizations", letter?.application_id],
    enabled: Boolean(letter?.application_id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("application_organizations")
        .select("organization_id, requested_amount")
        .eq("application_id", letter!.application_id);
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });

  const activeOrgs = (orgs ?? []).filter(o => o.active);
  const selectedOrgIds = (appOrgs ?? []).map(o => o.organization_id);
  const selectedOrgs = activeOrgs.filter(o => selectedOrgIds.includes(o.id));

  const org = selectedOrgs.find(o => o.id === letter?.organization_id) ?? null;

  const amounts = Object.fromEntries(
    (appOrgs ?? []).map(o => [o.organization_id, o.requested_amount])
  );

  const documents = (() => {
    const active = (checklist ?? []).filter(d => d.active);
    const scoped = active.filter(
      d => d.category_id === app?.category_id || d.category_id === null
    );
    return (scoped.length ? scoped : active).map(d => d.document_name);
  })();

  const letterApp = app
    ? {
        ref_no: app.ref_no,
        application_date: app.application_date,
        applicant_name: app.applicant_name,
        applicant_address: app.applicant_address,
        applicant_mobile: app.applicant_mobile,
        applicant_aadhaar: app.applicant_aadhaar,
        ration_card_no: app.ration_card_no,
        beneficiary_name: app.beneficiary_name,
        total_requirement: Number(app.total_requirement),
        applicant_contribution: Number(app.applicant_contribution),
        our_contribution: Number(app.our_contribution),
        other_received: Number(app.other_received),
        balance_required: Number(app.balance_required),
        bank_account_holder: app.bank_account_holder,
        bank_name: app.bank_name,
        bank_account_no: app.bank_account_no,
        bank_ifsc: app.bank_ifsc,
        categoryName: app.assistance_categories?.name ?? "",
      }
    : null;

  const printLetter = () => {
    const el = document.getElementById("letter-preview");
    if (el) {
      printElementAsPdf(
        `Letter-${letter?.organization_name || letter?.id}`,
        el
      );
    }
  };

  const whatsAppMessage = (() => {
    if (!app || !letter) {
      return "";
    }
    return [
      `*${settings?.["org_heading"] ?? "Sadharmik Assistance Desk"}*`,
      `To: ${letter.organization_name}`,
      `Recommendation letter ref. ${app.ref_no} dated ${shortDate(letter.letter_date)}`,
      `Beneficiary: ${app.beneficiary_name || app.applicant_name}`,
      `Amount requested: ${inr(Number(letter.requested_amount ?? 0))}`,
      `Balance required: ${inr(app.balance_required)}`,
      "",
      "Please find the recommendation letter attached.",
    ].join("\n");
  })();

  if (isLoading || !letter) {
    return (
      <Shell>
        <PageHeader title="Letter Detail" />
        <p className="text-muted-foreground">Loading letter…</p>
      </Shell>
    );
  }

  if (!letterApp || !org) {
    return (
      <Shell>
        <PageHeader title="Letter Detail" />
        <p className="text-muted-foreground">
          Letter or application data not found.
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/letters">← Back to Letter History</Link>
        </Button>
      </Shell>
    );
  }

  return (
    <Shell>
      <PageHeader
        title={`Letter ${app?.ref_no}`}
        subtitle={`${letter.organization_name} · ${shortDate(letter.letter_date)} · Valid until ${shortDate(letter.valid_until)}`}
        actions={
          <>
            <Button variant="outline" onClick={printLetter}>
              <Printer className="mr-2 h-4 w-4" /> Print / Save PDF
            </Button>
            <Button variant="outline" onClick={printLetter}>
              <Download className="mr-2 h-4 w-4" /> Download PDF
            </Button>
            {org?.contact_number && (
              <Button
                variant="outline"
                onClick={() =>
                  shareOnWhatsApp(org.contact_number, whatsAppMessage)
                }
              >
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
              </Button>
            )}
            <Button asChild variant="secondary">
              <Link to="/applications/$id" params={{ id: app!.id }}>
                Open Application
              </Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/letters">← Back</Link>
            </Button>
          </>
        }
      />

      <div id="letter-preview" className="print:p-0">
        <LetterSheet
          app={letterApp}
          org={org}
          allOrgs={selectedOrgs}
          amounts={amounts}
          documents={documents}
          settings={settings ?? {}}
          createdDate={letter.created_date}
          generatedDate={letter.generated_at?.slice(0, 10) ?? null}
        />
      </div>
    </Shell>
  );
}
